/*
 * GameGalaxy scripts on top of Canopy 7.4 (GG-659). Loaded deferred by snippets/gg-head.liquid.
 * Listeners are delegated to the document, because Canopy re-renders cart parts with innerHTML.
 */
(() => {
  // C1: "Meine Bestellung eilt nicht" is saved at once as cart attribute Lieferoptionen.
  document.addEventListener('change', (evt) => {
    const box = evt.target;
    if (!(box instanceof HTMLInputElement) || box.id !== 'slowerDelivery') return;
    fetch(`${window.theme?.routes?.cartUpdate || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Lieferoptionen: box.checked ? 'Langsamere Lieferung' : '' } })
    }).catch(() => {});
  });

  // H1.5: «Das könnte dir auch gefallen» on the cart page lies inside the cart form (#cart). When
  // Canopy inserts the cards there with innerHTML, the browser drops their own <form> tags (forms
  // cannot be nested in HTML), so «In den Warenkorb» submitted the cart form, the page reloaded and
  // nothing was added. Wrap each orphaned card form again with DOM methods (kept separate from the
  // cart form, its fields no longer go to the cart form) and let Canopy's product-form take it over.
  const cartForm = document.getElementById('cart');
  if (cartForm) {
    const repairCardForms = () => {
      cartForm.querySelectorAll('.cart-recommendations product-form').forEach((pf) => {
        if (pf.querySelector('form')) return;
        const form = document.createElement('form');
        form.method = 'post';
        form.action = window.theme?.routes?.cartAdd || '/cart/add';
        form.enctype = 'multipart/form-data';
        form.className = 'js-product-form';
        form.noValidate = true;
        [...pf.childNodes].forEach((node) => {
          if (!(node instanceof Element && node.matches('.js-form-error'))) form.append(node);
        });
        pf.append(form);
        if (!pf.form && typeof pf.init === 'function') pf.init();
      });
    };
    new MutationObserver(repairCardForms).observe(cartForm, { childList: true, subtree: true });
    repairCardForms();
  }

  // Desktop review D-9: width of the page scrollbar, measured while the page can scroll, for
  // gg.css to keep that room when the search preview locks the scroll (no sideways jump).
  const keepScrollbarWidth = () => {
    if (document.body.matches('.overlay-predictive-search, .overflow-hidden, .fixed')) return;
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--gg-scrollbar-width', `${Math.max(width, 0)}px`);
  };
  keepScrollbarWidth();
  window.addEventListener('resize', keepScrollbarWidth);

  // Columns (12.10.2026): collection description on phones cut to 3 lines by gg.css; the button
  // «Weiterlesen» / «Weniger anzeigen» opens and closes it and only shows while the text is longer
  // (snippets/gg-collection-description.liquid renders it when the text is probably longer).
  document.querySelectorAll('[data-gg-desc]').forEach((box) => {
    const text = box.querySelector('.gg-desc__text');
    const button = box.querySelector('.gg-desc__toggle');
    if (!text || !button) return;
    const check = () => {
      if (text.classList.contains('is-open')) return;
      if (getComputedStyle(text).overflow !== 'hidden') return; // larger screens: never cut
      button.hidden = text.scrollHeight <= text.clientHeight + 1;
    };
    button.addEventListener('click', () => {
      const open = !text.classList.contains('is-open');
      text.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.less : button.dataset.more;
      if (!open && button.getBoundingClientRect().top < 0) button.scrollIntoView({ block: 'center' });
    });
    check();
    window.addEventListener('resize', check);
    text.querySelectorAll('img').forEach((img) => img.addEventListener('load', check));
  });

  // Kachelgrösse (13.10.2026): tile size buttons in the toolbar of collections and search
  // (snippets/gg-tile-toggle.liquid). The size is kept per device class: below 769 px «phone»
  // (Gross l / Klein s, default s), from 769 px «desktop» (l / m / s, default l). It goes on <html>
  // as data-gg-tiles-<device> (the head script in snippets/gg-head.liquid sets it before the first
  // paint) and into localStorage; gg.css does the columns. The card at the top of the screen stays
  // in place when the grid reflows.
  const tilesDesktop = window.matchMedia('(min-width: 769px)');
  const tileDevice = () => (tilesDesktop.matches ? 'desktop' : 'phone');
  const tileSize = (device) => {
    const value = document.documentElement.getAttribute(`data-gg-tiles-${device}`);
    if (device === 'phone') return value === 'l' ? 'l' : 's';
    return value === 'm' || value === 's' ? value : 'l';
  };
  const syncTileButtons = () => {
    const size = tileSize(tileDevice());
    document.querySelectorAll('.gg-tiles__btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.ggTiles === size));
    });
  };
  document.addEventListener('click', (evt) => {
    const btn = evt.target instanceof Element ? evt.target.closest('.gg-tiles__btn') : null;
    if (!btn) return;
    const device = tileDevice();
    const size = btn.dataset.ggTiles;
    if (size === tileSize(device)) return;
    const toolbarBottom = document.querySelector('products-toolbar')?.getBoundingClientRect().bottom || 0;
    const anchor = [...document.querySelectorAll('.main-products-grid__results > .grid > li')]
      .find((li) => li.getBoundingClientRect().bottom > Math.max(toolbarBottom, 0));
    const before = anchor ? anchor.getBoundingClientRect().top : 0;
    document.documentElement.setAttribute(`data-gg-tiles-${device}`, size);
    try {
      localStorage.setItem(`gg-tiles-${device}`, size);
    } catch (e) {
      // private mode or blocked storage: the size holds for this page only
    }
    syncTileButtons();
    if (anchor && window.scrollY > 0) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
  });
  tilesDesktop.addEventListener('change', syncTileButtons);
  syncTileButtons();

  // D13: after logging in, back to the page the customer came from, otherwise into the account
  // (Shopify's default), never to the home page. A Wishlist King login (?wk-redirect=) keeps the
  // default, the app sends the customer back itself.
  const login = document.getElementById('customer_login');
  if (login && !login.querySelector('[name="return_to"]') && !new URLSearchParams(location.search).has('wk-redirect')) {
    let target = '';
    try {
      const ref = new URL(document.referrer);
      if (ref.origin === location.origin && !/^\/(account|challenge|checkouts?)(\/|$)/.test(ref.pathname)) {
        target = ref.pathname + ref.search;
      }
    } catch (e) {
      target = '';
    }
    if (target) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      input.value = target;
      login.append(input);
    }
  }
})();
